import { Formik, useFormik } from "formik";
import tw from "twrnc";

import Text from "@/components/shared/AppText";
import React, { useState } from "react";
import { ActivityIndicator, Image, View } from "react-native";

import AppConfig from "../../AppConfig";
import { roles } from "../globalUtils";
import { resetLogo, updateEmail, updateProfile } from "../http/events";
import { ChangeEmailSchema, ProfileSchema } from "../screens/Schemas";
import { useActionSheet } from "@/components/hooks/useActionSheet";
import { notify } from "./AppToast";
import UploadFile from "./UploadFile";
import AutoSave from "./AutoSave";
import Button, { ButtonRow } from "@/components/settings/Button";
import { FieldLabel, Hint, TextField } from "@/components/settings/Field";
import { UnsavedChanges } from "@/components/settings/SaveStatus";
import {
  ChoiceGroup,
  RowGroup,
  ToggleRow,
} from "@/components/settings/SettingRow";
import { SettingsSection } from "@/components/settings/SettingsPage";
import { colors, radius, text } from "@/components/settings/theme";
import { t } from "@/localization";

// Logo preview with Upload/Change and Remove (profile and event branding).
export const LogoPreviewAndUploader = ({
  logo_path,
  onDelete = () => {},
  onComplete = () => {},
  path,
  disclaimer,
}) => {
  const [logoLoading, setLogoLoading] = useState(false);

  return (
    <>
      <View
        style={[
          tw`items-center justify-center mb-3 p-4`,
          {
            minHeight: 96,
            borderWidth: 1,
            borderStyle: logo_path ? "solid" : "dashed",
            borderColor: colors.border,
            borderRadius: radius.card,
            backgroundColor: colors.surface,
          },
        ]}
      >
        {logoLoading ? (
          <ActivityIndicator color={colors.muted} />
        ) : logo_path ? (
          <Image
            source={{ uri: logo_path }}
            resizeMode="contain"
            accessibilityLabel={t("Logo")}
            style={tw`h-[60px] w-[220px]`}
          />
        ) : (
          <Text style={text.hint}>{t("No logo yet")}</Text>
        )}
      </View>
      <UploadFile
        path={path}
        bucket={AppConfig.REACT_APP_LOGO_BUCKET}
        renderButton={({ onPress, loading }) => (
          <ButtonRow align="start">
            <Button
              text={logo_path ? t("Change logo") : t("Upload logo")}
              icon="image-outline"
              size="md"
              fullWidth={false}
              loading={loading || logoLoading}
              onPress={onPress}
            />
            {!!logo_path && (
              <Button
                text={t("Remove")}
                variant="danger-text"
                size="md"
                fullWidth={false}
                onPress={() => onDelete()}
              />
            )}
          </ButtonRow>
        )}
        onStart={() => setLogoLoading(true)}
        onCancel={() => setLogoLoading(false)}
        onComplete={async (res) => {
          const logo_path = res?.url
            ?.replace(
              /^https:\/\/.*\.s3\.amazonaws\.com\//,
              "https://eventlivelogos.b-cdn.net/"
            )
            .replace(/\.[^/.]+$/, ".png");

          // The CDN serves the trimmed PNG a moment after the upload.
          const checkLogoAccessible = async () => {
            try {
              const response = await fetch(logo_path, {
                method: "HEAD",
              });
              if (response.ok) {
                onComplete(logo_path);
                setLogoLoading(false);
              } else {
                setTimeout(checkLogoAccessible, 1000);
              }
            } catch (error) {
              console.error("Fetch error:", error);
              setTimeout(checkLogoAccessible, 1000);
            }
          };

          checkLogoAccessible();
        }}
      />
      {!!disclaimer && <Hint style={tw`mt-2`}>{disclaimer}</Hint>}
    </>
  );
};

// The sign-in email changes at once on the server (no confirmation email), so
// it has its own button and a confirmation instead of auto-save.
const ChangeEmail = ({ email, refetchProfile }) => {
  const { showActionSheetWithOptions } = useActionSheet();
  const current = (email || "").toLowerCase();
  return (
    <Formik
      initialValues={{ email: email || "" }}
      validationSchema={ChangeEmailSchema}
      validateOnBlur={false}
      onSubmit={async (values, actions) => {
        const next = values.email.trim().toLowerCase();
        try {
          await updateEmail({ email: next });
          await refetchProfile?.();
          actions.resetForm({ values: { email: next } });
          notify.success(
            t("Email changed"),
            t("Sign in with {email} from now on.", { email: next })
          );
        } catch (err) {
          actions.setSubmitting(false);
          actions.setFieldError(
            "email",
            /duplicate/i.test(String(err?.message || err))
              ? t("Another EventLive account already uses this email.")
              : t("Couldn't change the email. Try again.")
          );
        }
      }}
    >
      {(f) => {
        const next = f.values.email.trim().toLowerCase();
        const changed = next !== current;
        const error =
          changed && f.values.email
            ? f.errors.email
            : f.touched.email && f.errors.email;
        const canSubmit =
          changed && !!next && !f.errors.email && !f.isSubmitting;
        const confirm = () =>
          showActionSheetWithOptions(
            {
              options: [t("Change email"), t("Cancel")],
              cancelButtonIndex: 1,
              destructiveButtonIndex: 0,
              title: t("Change your sign-in email?"),
              message: t(
                "You'll sign in with {email} from now on. The change is immediate, with no confirmation email, so check the address.",
                { email: next }
              ),
            },
            (buttonIndex) => buttonIndex === 0 && f.handleSubmit()
          );
        return (
          <View>
            <UnsavedChanges dirty={changed} />
            <TextField
              label={t("Sign-in email")}
              help="profile.email"
              value={f.values.email}
              onChangeText={f.handleChange("email")}
              onBlur={f.handleBlur("email")}
              placeholder={t("you@example.com")}
              keyboardType="email-address"
              textContentType="emailAddress"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect={false}
              error={error || null}
              onSubmitEditing={() => canSubmit && confirm()}
              last
            />
            {changed && (
              <ButtonRow style={tw`mt-3`}>
                <Button
                  text={t("Cancel")}
                  variant="tertiary"
                  size="md"
                  fullWidth={false}
                  disabled={f.isSubmitting}
                  onPress={() => f.resetForm()}
                />
                <Button
                  text={t("Change email")}
                  variant="primary"
                  size="md"
                  fullWidth={false}
                  loading={f.isSubmitting}
                  disabled={!canSubmit}
                  onPress={confirm}
                />
              </ButtonRow>
            )}
          </View>
        );
      }}
    </Formik>
  );
};

// Profile: name, sign-in email, account type and (business accounts) role
// and the branding used on every event page. Everything but the email saves
// automatically; the header shows the result.
const UpdateProfileForm = ({
  refetchProfile,
  profile,
  logo_path,
  broadcaster,
}) => {
  const f = useFormik({
    initialValues: {
      first_name: profile.first_name || "",
      last_name: profile.last_name || "",
      account_type: profile.account_type || "individual",
      broadcaster_role: profile.broadcaster_role || null,
      role_other: profile.role_other || "",
      company_name: profile.company_name || "",
      company_url: profile.company_url || "",
      no_branding: !!profile.no_branding,
    },
    validationSchema: ProfileSchema,
    onSubmit: async (values) => {
      await updateProfile(values);
      await refetchProfile?.();
    },
  });
  const business = f.values.account_type === "business";

  return (
    <>
      <AutoSave {...f} />

      <SettingsSection first title={t("Your details")}>
        <TextField
          label={t("First name")}
          value={f.values.first_name}
          onChangeText={f.handleChange("first_name")}
          onBlur={f.handleBlur("first_name")}
          textContentType="givenName"
          autoComplete="given-name"
          error={f.touched.first_name ? f.errors.first_name : null}
        />
        <TextField
          label={t("Last name")}
          optional
          value={f.values.last_name}
          onChangeText={f.handleChange("last_name")}
          onBlur={f.handleBlur("last_name")}
          textContentType="familyName"
          autoComplete="family-name"
          error={f.touched.last_name ? f.errors.last_name : null}
        />
        <ChangeEmail email={profile.email} refetchProfile={refetchProfile} />
      </SettingsSection>

      <SettingsSection title={t("Account type")} help="profile.accountType">
        <ChoiceGroup
          accessibilityLabel={t("Account type")}
          value={f.values.account_type}
          onChange={(value) => f.setFieldValue("account_type", value)}
          options={[
            {
              value: "individual",
              label: t("Personal"),
              description: t(
                "Your own events, like a wedding, birthday or memorial."
              ),
            },
            {
              value: "business",
              label: t("Business"),
              description: t(
                "Events for clients or your organization, with your branding and a Video Wall."
              ),
            },
          ]}
        />
      </SettingsSection>

      {business && (
        <SettingsSection title={t("What best describes you?")}>
          <ChoiceGroup
            accessibilityLabel={t("What best describes you?")}
            value={f.values.broadcaster_role}
            onChange={(value) => f.setFieldValue("broadcaster_role", value)}
            options={roles.map(({ value, label }) => ({
              value,
              label,
              children:
                value === "other" ? (
                  <TextField
                    label={t("Your role")}
                    value={f.values.role_other}
                    onChangeText={f.handleChange("role_other")}
                    onBlur={f.handleBlur("role_other")}
                    placeholder={t("e.g. Sports club")}
                    error={f.touched.role_other ? f.errors.role_other : null}
                    last
                  />
                ) : undefined,
            }))}
          />
        </SettingsSection>
      )}

      {business && (
        <SettingsSection
          title={t("Event page branding")}
          description={t(
            "Your logo or company name at the top of all your event pages."
          )}
        >
          <TextField
            label={t("Company name")}
            optional
            value={f.values.company_name}
            onChangeText={f.handleChange("company_name")}
            onBlur={f.handleBlur("company_name")}
            placeholder={t("e.g. Anna & Mark Weddings")}
            hint={t("Shown when you haven't uploaded a logo.")}
            error={f.touched.company_name ? f.errors.company_name : null}
          />
          <TextField
            label={t("Website")}
            optional
            value={f.values.company_url}
            onChangeText={f.handleChange("company_url")}
            onBlur={f.handleBlur("company_url")}
            placeholder={t("e.g. annaandmark.com")}
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="url"
            hint={t("Your logo or company name links to it.")}
            error={f.touched.company_url ? f.errors.company_url : null}
          />
          {!f.values.no_branding && (
            <View style={tw`mb-4`}>
              <FieldLabel label={t("Logo")} optional help="profile.logo" />
              <LogoPreviewAndUploader
                path={broadcaster}
                logo_path={logo_path}
                disclaimer={t(
                  "PNG or JPG; it's trimmed and resized automatically. Horizontal logos work best."
                )}
                onDelete={async () => {
                  await resetLogo(broadcaster);
                  await refetchProfile?.();
                }}
                onComplete={async () => {
                  await refetchProfile?.();
                }}
              />
            </View>
          )}
          <RowGroup>
            <ToggleRow
              label={t("Remove all branding")}
              description={t(
                "No logo, company name or EventLive logo at the top of your event pages."
              )}
              help="profile.noBranding"
              value={!!f.values.no_branding}
              onValueChange={(value) => f.setFieldValue("no_branding", value)}
            />
          </RowGroup>
        </SettingsSection>
      )}
    </>
  );
};

export default UpdateProfileForm;
